import React, { useState } from 'react'

import { Flex, Modal } from '@driftt/dds'
import { faBullseyeArrow } from '@driftt/dds.icons/light/faBullseyeArrow'
import { faFilter } from '@driftt/dds.icons/light/faFilter'
import { faGear } from '@driftt/dds.icons/light/faGear'
import { access } from '@driftt/traffic-control'

import { DeserializedSavedFilter } from 'api/AccountActivityAPI/types'
import DeleteFilterModal from 'components/DeleteFilterModal'
import SaveFilterModal from 'components/SaveFilterModal'
import useSavedFilters from 'hooks/useSavedFilters'
import { FilterGroup } from 'utils/filters'

import AccountFitPreferences from './AccountFitPreferences'
import AdvancedPreferences from './AdvancedPreferences'
import ManageSavedFilters from './ManageFilters'
import PreferencesMenuButton from './MenuButton'

interface PrefrencesModalProps {
  open: boolean
  setModalOpen: (open: boolean) => void
  sharedFilters: DeserializedSavedFilter[]
  privateFilters: DeserializedSavedFilter[]
  defaultTab?: PrefrencesMenuItem
}
export enum PrefrencesMenuItem {
  ACCOUNT_FIT = 'ACCOUNT_FIT',
  FILTERS = 'FILTERS',
  ADVANCED = 'ADVANCED'
}
const PreferencesModal = ({
  open,
  setModalOpen,
  sharedFilters,
  privateFilters,
  defaultTab
}: PrefrencesModalProps) => {
  const isAdmin = access.isAdmin()
  //preference modal menu selection
  const [activeMenuItem, setActiveMenuItem] = useState<PrefrencesMenuItem>(
    defaultTab ? defaultTab : isAdmin ? PrefrencesMenuItem.ACCOUNT_FIT : PrefrencesMenuItem.FILTERS
  )
  const [needsReload, setNeedsReload] = useState<boolean>(false)

  const contentSwitch = () => {
    switch (activeMenuItem) {
      case PrefrencesMenuItem.ACCOUNT_FIT:
        return <AccountFitPreferences onPreferencesUpdated={() => setNeedsReload(true)} />
      case PrefrencesMenuItem.FILTERS:
        return (
          <ManageSavedFilters
            sharedFilters={sharedFilters}
            privateFilters={privateFilters}
            openDeleteModal={openDeleteModal}
            setSavedFilter={setSavedFilter}
            openEditModal={openEditModal}
            closeDeleteModal={closeDeleteModal}
            savedFilter={savedFilter}
          />
        )
      case PrefrencesMenuItem.ADVANCED:
        return <AdvancedPreferences setFiltersUpdated={() => setNeedsReload(true)} />
      default:
        return null
    }
  }

  const handlePreferencesModalClose = () => {
    // The #showPrefs hash indicates that the modal should be opened when linking to the page
    if (window.location.hash === '#showPrefs') {
      window.location.hash = ''
    }
    if (needsReload) {
      window.location.reload()
    } else {
      setModalOpen(false)
    }
  }

  //filter management - edit/delete modals
  const { removeSavedFilter } = useSavedFilters()
  const [showSavedFilterModal, setShowSavedFilterModal] = useState<boolean>(false)
  const [editableFilterGroups, setEditableFilterGroups] = useState<FilterGroup[]>([])
  const [editableSavedFilter, setEditableSavedFilter] = useState<
    DeserializedSavedFilter | undefined
  >(undefined)
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [savedFilter, setSavedFilter] = useState<DeserializedSavedFilter | null>(null)

  const openEditModal = (filter?: DeserializedSavedFilter, editableGroups?: FilterGroup[]) => {
    setShowSavedFilterModal(true)
    setEditableSavedFilter(filter)
    setEditableFilterGroups(editableGroups || [])
    setModalOpen(false)
  }

  const onCloseEditModal = () => {
    setShowSavedFilterModal(false)
    setEditableSavedFilter(undefined)
    setEditableFilterGroups([])
    setModalOpen(true)
  }

  const openDeleteModal = (filter: DeserializedSavedFilter) => {
    setSavedFilter(filter)
    setDeleteModalOpen(true)
    setModalOpen(false)
  }

  const closeDeleteModal = () => {
    setDeleteModalOpen(false)
    setModalOpen(true)
  }

  return (
    <>
      <Modal.Root open={open} onClose={handlePreferencesModalClose}>
        <Modal.Dialog
          size="x-large"
          variant="info"
          fullWidth
          xlColumnSize="100-0"
          css={{ height: '100%' }}
          className="preferencesModal">
          <Modal.Title title="Preferences" />
          <Modal.Content css={{ overflow: 'hidden', maxHeight: '100%' }}>
            <Flex fullWidth fullHeight direction="row" gap={2}>
              <Flex direction="column" gap={1}>
                {isAdmin && (
                  <PreferencesMenuButton
                    icon={faBullseyeArrow}
                    title="Account fit"
                    isNew={true}
                    isActive={activeMenuItem === PrefrencesMenuItem.ACCOUNT_FIT}
                    onClick={() => setActiveMenuItem(PrefrencesMenuItem.ACCOUNT_FIT)}
                  />
                )}
                <PreferencesMenuButton
                  icon={faFilter}
                  title="Saved filters"
                  isActive={activeMenuItem === PrefrencesMenuItem.FILTERS}
                  onClick={() => setActiveMenuItem(PrefrencesMenuItem.FILTERS)}
                />
                {isAdmin && (
                  <PreferencesMenuButton
                    icon={faGear}
                    title="Advanced"
                    isActive={activeMenuItem === PrefrencesMenuItem.ADVANCED}
                    onClick={() => setActiveMenuItem(PrefrencesMenuItem.ADVANCED)}
                  />
                )}
              </Flex>
              <Flex.Item css={{ overflow: 'auto', height: '100%', width: '100%' }}>
                {contentSwitch()}
              </Flex.Item>
            </Flex>
          </Modal.Content>
        </Modal.Dialog>
      </Modal.Root>
      {isDeleteModalOpen && savedFilter && (
        <DeleteFilterModal
          savedFilter={savedFilter}
          isModalOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onRemoveSavedFilter={removeSavedFilter}
        />
      )}
      {showSavedFilterModal && (
        <SaveFilterModal
          editableGroups={editableFilterGroups}
          savedFilter={editableSavedFilter}
          onClose={onCloseEditModal}
        />
      )}
    </>
  )
}

export default PreferencesModal
